import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Grid, Text, Select, Input, Checkbox, Stack, Button, Spinner, useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  getTestOptions, estimateTest, createTest, getTests, TEST_PHASE, errorMessage, formatScore,
} from 'api/administrador/monitoring/aiGrading';
import { StatusPill } from './common';
import AiPagination from './AiPagination';

function ModeCard({ value, current, title, text, onChange }) {
  const active = current === value;
  return (
    <Flex
      as="label" gap={2.5} align="flex-start" p={3} borderRadius="10px" cursor="pointer"
      border={active ? '2px solid #574feb' : '1px solid'} borderColor={active ? '#574feb' : 'gray.200'} bg={active ? '#F5F3FF' : 'white'}
    >
      <input type="radio" name="test-mode" checked={active} onChange={() => onChange(value)} style={{ marginTop: 3, accentColor: '#574feb' }} />
      <Box>
        <Text fontSize="14px" fontWeight="600">{title}</Text>
        <Text fontSize="12px" color="gray.600">{text}</Text>
      </Box>
    </Flex>
  );
}

function summaryLine(t) {
  const s = t.summary;
  if (t.phase === 'running') return null;
  if (t.phase === 'failed') return 'La prueba se interrumpió.';
  if (t.filters.mode === 'graded') {
    return s.mae !== undefined
      ? `Diferencia promedio con el docente ${formatScore(s.mae)} · ${s.within2} % a ±2 · IA ${formatScore(s.avgScore)} / docente ${formatScore(s.teacherAvg)}`
      : `${s.graded} calificadas`;
  }
  const base = `${s.graded} calificadas · ${s.deferred + s.errors} al docente · nota prom. ${formatScore(s.avgScore)}`;
  return t.published ? `${base} · ${t.published} publicadas` : base;
}

export default function TestsTab({ tick, onOpenTest }) {
  const toast = useToast();
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState({
    program: '', subjectIds: [], mode: 'pending', since: dayjs().subtract(30, 'day').format('YYYY-MM-DD'), sample: 50,
  });
  const [estimate, setEstimate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [list, setList] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    getTestOptions().then(setOptions).catch(() => setOptions({ programs: [], maxSample: 100 }));
  }, []);

  const program = useMemo(() => options?.programs.find((p) => p.key === form.program), [options, form.program]);

  // Estimación: cuántas entregas hay, tiempo y costo.
  useEffect(() => {
    if (!form.subjectIds.length) {
      setEstimate(null);
      return undefined;
    }
    const t = setTimeout(() => {
      estimateTest({ subjectIds: form.subjectIds, mode: form.mode, since: form.since, sample: form.sample })
        .then(setEstimate)
        .catch(() => setEstimate(null));
    }, 300);
    return () => clearTimeout(t);
  }, [form.subjectIds, form.mode, form.since, form.sample]);

  const loadList = useCallback(async () => {
    try {
      setList(await getTests({ page, pageSize: 10 }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[calificación] error cargando pruebas:', e);
    }
  }, [page]);

  useEffect(() => { loadList(); }, [loadList, tick]);

  // Mientras haya una prueba en curso, el avance se refresca cada 5 s.
  const running = list?.data?.some((t) => t.phase === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(loadList, 5000);
    return () => clearInterval(id);
  }, [running, loadList]);

  const run = async () => {
    setCreating(true);
    try {
      const r = await createTest({ subjectIds: form.subjectIds, mode: form.mode, since: form.since, sample: form.sample });
      toast({ status: 'success', title: `Prueba #${r.id} en curso (${r.sample} entregas)` });
      setPage(1);
      loadList();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    } finally {
      setCreating(false);
    }
  };

  const toggleSubject = (id, on) => setForm((f) => ({ ...f, subjectIds: on ? [...f.subjectIds, id] : f.subjectIds.filter((x) => x !== id) }));

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
      <Grid templateColumns={{ base: 'minmax(0, 1fr)', lg: '380px minmax(0, 1fr)' }}>
        <Box as="form" p={5} borderRight={{ lg: '1px solid' }} borderColor="gray.200" onSubmit={(e) => { e.preventDefault(); run(); }}>
          <Text fontSize="16px" fontWeight="700">Nueva prueba</Text>
          <Text fontSize="13px" color="gray.500" mt={1} mb={4}>Califica una muestra sin publicar nada. Revisas el resultado y decides.</Text>

          <Stack spacing={4}>
            <Box>
              <Text as="label" htmlFor="test-program" fontSize="13px" fontWeight="600">Programa</Text>
              <Select
                id="test-program" mt={1.5} borderRadius="8px" placeholder={options ? 'Elige un programa' : 'Cargando…'}
                value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value, subjectIds: [] })}
              >
                {(options?.programs || []).map((p) => <option key={p.key} value={p.key}>{p.name}{p.type ? ` · ${p.type}` : ''}</option>)}
              </Select>
            </Box>

            {program && (
              <Box>
                <Flex justify="space-between" align="baseline">
                  <Text fontSize="13px" fontWeight="600">Materias</Text>
                  <Button
                    size="xs" variant="link" color="#574feb"
                    onClick={() => setForm({ ...form, subjectIds: form.subjectIds.length === program.subjects.length ? [] : program.subjects.map((s) => s.id) })}
                  >
                    {form.subjectIds.length === program.subjects.length ? 'Quitar todas' : 'Elegir todas'}
                  </Button>
                </Flex>
                <Stack mt={1.5} spacing={1.5} maxH="170px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="8px" p={2.5}>
                  {program.subjects.map((s) => (
                    <Checkbox key={s.id} colorScheme="purple" isChecked={form.subjectIds.includes(s.id)} onChange={(e) => toggleSubject(s.id, e.target.checked)}>
                      <Text fontSize="14px">{s.name}</Text>
                    </Checkbox>
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Text fontSize="13px" fontWeight="600" mb={1.5}>Qué entregas</Text>
              <Stack spacing={2}>
                <ModeCard value="pending" current={form.mode} onChange={(mode) => setForm({ ...form, mode })}
                  title="Pendientes sin nota" text="Si salen bien, se pueden publicar." />
                <ModeCard value="graded" current={form.mode} onChange={(mode) => setForm({ ...form, mode })}
                  title="Ya calificadas por el docente" text="Solo para comparar. Nunca se publican." />
              </Stack>
            </Box>

            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={2.5}>
              <Box>
                <Text as="label" htmlFor="test-since" fontSize="13px" fontWeight="600">Enviadas desde</Text>
                <Input id="test-since" type="date" mt={1.5} borderRadius="8px" value={form.since} onChange={(e) => setForm({ ...form, since: e.target.value })} />
              </Box>
              <Box>
                <Text as="label" htmlFor="test-sample" fontSize="13px" fontWeight="600">Muestra</Text>
                <Input
                  id="test-sample" type="number" min={1} max={options?.maxSample || 100} mt={1.5} borderRadius="8px"
                  value={form.sample} onChange={(e) => setForm({ ...form, sample: Math.min(Number(e.target.value) || 1, options?.maxSample || 100) })}
                />
              </Box>
            </Grid>

            <Box px={3} py={2.5} borderRadius="8px" bg="gray.50" fontSize="13px" color="gray.600">
              {estimate
                ? `${estimate.available} disponibles · se toman ${estimate.sample} al azar · ≈ ${estimate.minutes} min · ≈ $${String(estimate.cost).replace('.', ',')}`
                : 'Elige programa y materias para ver cuántas entregas hay.'}
            </Box>

            <Button
              type="submit" isLoading={creating} isDisabled={!estimate?.sample}
              bg="#574feb" color="white" _hover={{ bg: '#4338CA' }} borderRadius="10px" h="44px"
            >
              Correr prueba
            </Button>
          </Stack>
        </Box>

        <Box p={5}>
          <Text fontSize="16px" fontWeight="700" mb={3}>Pruebas recientes</Text>
          {!list && <Flex justify="center" py={8}><Spinner color="#574feb" /></Flex>}
          {list && !list.data.length && <Text fontSize="sm" color="gray.500">Aún no hay pruebas.</Text>}
          <Stack spacing={3}>
            {(list?.data || []).map((t) => {
              const phase = TEST_PHASE[t.phase] || TEST_PHASE.running;
              const pct = t.progress.total ? Math.round((t.progress.done / t.progress.total) * 100) : 0;
              const line = summaryLine(t);
              return (
                <Box key={t.id} border="1px solid" borderColor="gray.200" borderRadius="12px" px={4} py={3.5}>
                  <Flex justify="space-between" align="flex-start" gap={3}>
                    <Box minW={0}>
                      <Text fontSize="14px" fontWeight="600" isTruncated>
                        #{t.id} · {(t.filters.subjectNames || []).join(', ') || 'Materias'}{t.filters.mode === 'graded' ? ' (comparación)' : ''}
                      </Text>
                      <Text fontSize="12px" color="gray.500">
                        {t.filters.mode === 'graded' ? 'Ya calificadas' : 'Pendientes'} · {t.progress.total} entregas
                        {t.launchedBy ? ` · ${t.launchedBy}` : ''} · {dayjs(t.startedAt).fromNow()}
                      </Text>
                    </Box>
                    <StatusPill label={phase.label} tone={phase.tone} />
                  </Flex>
                  {t.phase === 'running' && (
                    <Box mt={2.5}>
                      <Box h="6px" borderRadius="999px" bg="gray.100"><Box h="6px" w={`${pct}%`} borderRadius="999px" bg="#574feb" transition="width .4s" /></Box>
                      <Text fontSize="12px" color="gray.600" mt={1}>{t.progress.done} de {t.progress.total} calificadas</Text>
                    </Box>
                  )}
                  {line && (
                    <Flex justify="space-between" align="center" mt={2.5} gap={3}>
                      <Text fontSize="13px" color="gray.700">{line}</Text>
                      {t.phase !== 'failed' && (
                        <Button variant="link" color="#574feb" fontSize="14px" onClick={() => onOpenTest(t.id)} flexShrink={0}>
                          {t.phase === 'review' ? 'Revisar resultado' : t.phase === 'compared' ? 'Ver comparación' : 'Ver detalle'}
                        </Button>
                      )}
                    </Flex>
                  )}
                </Box>
              );
            })}
          </Stack>
          <Box mx={-5} mb={-5} mt={3}>
            <AiPagination pagination={list?.pagination} noun="pruebas" onPageChange={setPage} />
          </Box>
        </Box>
      </Grid>
    </Box>
  );
}
